// CSS imports - loaded early to ensure styles work in production build
// Design system variables - MUST be first to define CSS custom properties
import './styles/variables.css';
import './App.css';
import './AdminDashboard.css';
import './BenefitsView.css';
import './ExperimentsView.css';
import './LandingView.css';
import './ReportExperimentsView.css';
import './ClimateRiskView.css';
import './ReportSourcesView.css';
// SearchMetricsView.css converted to SearchMetricsView.module.css - imported in component
import './SettingsView.css';
import './StatsView.css';
import './TaskStatusView.css';
import './TasksView.css';
import './TaskDetailView.css';
import './SearchTargetPipelineView.css';
import './WebSearchView.css';
// Component CSS imports
import './components/BottomNavigation.css';
import './components/CookieBanner.css';
import './components/Feedback.css';
import './components/FeedbackManagement.css';
import './components/ImpersonationOverlay.css';
import './components/Imprint.css';
import './components/LabelEditor.css';
import './components/LanguageSwitcher.css';
import './components/LoginModal.css';
import './components/NavSubmenu.css';
import './components/PDFViewer.css';
import './components/PrivacyPolicy.css';
import './components/ProfileMenu.css';
import './components/Select.css';
import './components/SettingsMenu.css';
import './components/SystemInfoPopup.css';
import './components/TrialStatusBanner.css';
import './components/TypeaheadDropdown.css';
import './components/UpgradeButton.css';
// Component subdirectory CSS - Ground Truth
import './components/groundTruth/GroundTruthTable.css';
// Tasks CSS
import './components/tasks/TaskCard.css';
import './components/tasks/TasksList.css';
import './components/tasks/TaskEditModal.css';
import './i18n'; // Import i18n configuration
import env from './utils/env';

import BondsView from './BondsView.tsx';
import BondDetailView from './BondDetailView.tsx';
import ReportSourcesView from './ReportSourcesView.tsx';
import EmbeddingsSearchView from './EmbeddingsSearchView.tsx';
import SearchTargetsView from './SearchTargetsView.tsx';
import SearchResultsView from './SearchResultsView.tsx';
import ReportSourceCandidatesView from './ReportSourceCandidatesView.tsx';
import ReportSourceCandidateDetailView from './ReportSourceCandidateDetailView.tsx';
import WebSearchView from './WebSearchView.tsx';
import UserManagementView from './UserManagementView.tsx';
import SettingsView from './SettingsView.tsx';
import LoginModal from './components/LoginModal.tsx';
import ProfileMenu from './components/ProfileMenu.tsx';
import SettingsMenu from './components/SettingsMenu.tsx';
import CookieBanner from './components/CookieBanner.tsx';
import SystemInfoPopup from './components/SystemInfoPopup.tsx';
import ImpersonationOverlay from './components/ImpersonationOverlay.tsx';
import TrialStatusBanner from './components/TrialStatusBanner.tsx';
import BottomNavigation from './components/BottomNavigation.tsx';
import NavSubmenu from './components/NavSubmenu.tsx';
import { useEffect, useState, useCallback, useRef } from "react";
import { HashRouter as Router, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import VerifyEmail from './VerifyEmail';
import VerifyEmailTest from './VerifyEmailTest';
import ResetPassword from './ResetPassword';
import UpgradeButton from './components/UpgradeButton';
import SubscriptionSuccess from './SubscriptionSuccess';
import SubscriptionCanceled from './SubscriptionCanceled';
import SubscriptionManage from './SubscriptionManage';
import BenefitsView from './BenefitsView';
import LandingView from './LandingView';
import PrivacyPolicy from './components/PrivacyPolicy';
import Imprint from './components/Imprint';
import userActivityService, { useUserTracking } from './services/UserActivityService';
import * as Sentry from '@sentry/react';
import ReportsView from './ReportsView';
import ReportDetailView from './ReportDetailView';
import ExperimentsView from './ExperimentsView';
import ExperimentDetailView from './ExperimentDetailView';
import ExperimentCreateEdit from './ExperimentCreateEdit';
import './ExperimentsView.css';
import ReportExperimentsView from './ReportExperimentsView';
import StatsView from './StatsView';
import SearchMetricsView from './SearchMetricsView';
import TaskStatusView from './TaskStatusView';
import TasksView from './TasksView';
import TaskDetailView from './TaskDetailView';
import AdminDashboard from './AdminDashboard';
import Feedback from './components/Feedback';
import FeedbackManagement from './components/FeedbackManagement';
import GroundTruthListView from './GroundTruthListView';
import GroundTruthDetailView from './GroundTruthDetailView';
import ClimateRiskView from './ClimateRiskView';
import ClimateRiskDetailView from './ClimateRiskDetailView';
import AdminSearchTargetsView from './AdminSearchTargetsView';
import AdminSearchTargetDetailView from './AdminSearchTargetDetailView';
import SearchTargetPipelineView from './SearchTargetPipelineView';
import AdminSearchAttemptsView from './AdminSearchAttemptsView';
import AdminSearchAttemptDetailView from './AdminSearchAttemptDetailView';

// Get version info from build-time globals
const getVersionInfo = () => {
    try {
        return {
            version: __APP_VERSION__,
            buildTime: __BUILD_TIME__
        };
    } catch {
        // Fallback for development
        return {
            version: 'dev',
            buildTime: new Date().toISOString()
        };
    }
};

// Cookie consent interface
interface CookieConsent {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
}

// Component to track page views
function PageViewTracker() {
    const location = useLocation();
    const { trackPageView } = useUserTracking();

    useEffect(() => {
        // Track page view whenever location changes
        const pageName = location.pathname === '/' ? 'home' : location.pathname.slice(1);
        trackPageView(pageName, {
            fullPath: location.pathname,
            search: location.search,
            hash: location.hash,
        });
    }, [location, trackPageView]);

    return null;
}

function AppContent() {
    const { user, login, logout, isAuthenticated, hasRole, authLoaded, isImpersonating } = useAuth();
    const { t } = useTranslation();
    const { trackUserAction, trackError, deviceInfo } = useUserTracking();
    const navigate = useNavigate();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'login' | 'signup'>('login');
    const [sessionExpired, setSessionExpired] = useState(false);
    const sessionExpiredRef = useRef(false);
    // Theme type: 'light' | 'dark' | 'system'
    type ThemeMode = 'light' | 'dark' | 'system';

    const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
        // Check if user has a preference stored
        const savedTheme = localStorage.getItem('themeMode') as ThemeMode;
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
            return savedTheme;
        }
        // Default to system mode for new users
        return 'system';
    });

    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('themeMode') as ThemeMode;
        if (savedTheme === 'light') return false;
        if (savedTheme === 'dark') return true;
        // For 'system' mode or no saved preference, use system preference
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });
    const [showSystemInfoPopup, setShowSystemInfoPopup] = useState(false);
    const [, setCookieConsent] = useState<CookieConsent>({
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
    });

    const apiBaseUrl = env.apiUrl;
    const versionInfo = getVersionInfo();

    // Handle cookie consent changes
    const handleCookieConsentChange = useCallback((consent: CookieConsent) => {
        setCookieConsent(consent);

        // Update Sentry configuration based on consent
        const sentryClient = Sentry.getClient();
        if (sentryClient) {
            const options = sentryClient.getOptions();
            if (consent.analytics) {
                // Enable performance monitoring and session replay
                options.tracesSampleRate = 0.1;
            } else {
                // Disable performance monitoring if analytics not consented
                options.tracesSampleRate = 0;
            }
        }

        // Track consent change
        trackUserAction('cookie_consent_changed', 'cookie_banner', {
            consent: consent,
            deviceType: deviceInfo?.deviceType,
        });
    }, [trackUserAction, deviceInfo?.deviceType]);

    // Set up user tracking when authentication changes
    useEffect(() => {
        if (isAuthenticated && user) {
            userActivityService.setUser(user.id, user.role);

            // Set additional Sentry context
            Sentry.setContext('user_device', {
                type: deviceInfo?.deviceType,
                os: deviceInfo?.os,
                browser: deviceInfo?.browser,
                screenResolution: deviceInfo?.screenResolution,
            });
        } else {
            userActivityService.clearUser();
            Sentry.setUser(null);
        }
    }, [isAuthenticated, user, deviceInfo]);

    useEffect(() => {
        // Only allow dark mode if authenticated
        if (isAuthenticated && isDarkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
        // Save theme mode preference
        localStorage.setItem('themeMode', themeMode);

        // Track theme change
        trackUserAction('theme_changed', 'app', {
            themeMode: themeMode,
            isDarkMode: isDarkMode,
            isAuthenticated: isAuthenticated
        });
    }, [isDarkMode, themeMode, trackUserAction, isAuthenticated]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const darkModeHandler = (e: MediaQueryListEvent) => {
            // Only change theme if we're in 'system' mode
            if (themeMode === 'system') {
                setIsDarkMode(e.matches);
            }
        };

        // Modern browsers
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', darkModeHandler);
            return () => mediaQuery.removeEventListener('change', darkModeHandler);
        }

        return undefined;
    }, [themeMode]);

    useEffect(() => {
        document.body.classList.toggle('dark-mode', isDarkMode);
        console.log(`App Version: ${versionInfo.version}, Built: ${versionInfo.buildTime}`);
        console.log('Device Info:', deviceInfo);
    }, [isDarkMode, versionInfo.version, versionInfo.buildTime, deviceInfo]);

    // Manage body class for impersonation overlay spacing
    useEffect(() => {
        document.body.classList.toggle('impersonating', isImpersonating);
    }, [isImpersonating]);

    // Global API error handler for 401/403
    useEffect(() => {
        // Patch window.fetch
        const origFetch = window.fetch;
        window.fetch = async (...args) => {
            const response = await origFetch(...args);
            // Only show login modal if:
            // 1. Response is 401/403 (auth error)
            // 2. Auth state has loaded (authLoaded is true)
            // 3. User is not authenticated OR token might have expired
            if ((response.status === 401 || response.status === 403) && authLoaded) {
                // If user appears authenticated but got 401/403, their token might have expired
                // If user is not authenticated, this is a legitimate auth failure
                // In both cases, show login modal
                if (!sessionExpiredRef.current) {
                    setSessionExpired(true);
                    sessionExpiredRef.current = true;
                }
            }
            return response;
        };
        // Patch axios if present
        if ((window as any).axios && (window as any).axios.interceptors) {
            (window as any).axios.interceptors.response.use(
                (response: any) => response,
                (error: any) => {
                    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                        if (authLoaded && !sessionExpiredRef.current) {
                            setSessionExpired(true);
                            sessionExpiredRef.current = true;
                        }
                    }
                    return Promise.reject(error);
                }
            );
        }
        return () => {
            window.fetch = origFetch;
        };
    }, [authLoaded]);

    // Handle legacy verification email links (before HashRouter migration)
    useEffect(() => {
        // Check if we're on a legacy verification link
        if (window.location.pathname === '/verify-email' && window.location.search) {
            const params = new URLSearchParams(window.location.search);
            const token = params.get('token');
            if (token) {
                // Redirect to the new hash-based URL
                window.location.href = `${window.location.origin}/#/verify-email?token=${token}`;
            }
        }

        // Also handle verify-email-test for testing
        if (window.location.pathname === '/verify-email-test' && window.location.search) {
            window.location.href = `${window.location.origin}/#/verify-email-test${window.location.search}`;
        }

        // Handle legacy password reset links
        if (window.location.pathname === '/reset-password' && window.location.search) {
            const params = new URLSearchParams(window.location.search);
            const token = params.get('token');
            if (token) {
                window.location.href = `${window.location.origin}/#/reset-password?token=${token}`;
            }
        }
    }, []);

    // When user logs in, clear session expired state
    const handleLogin = (user: any, token: string) => {
        login(user, token);
        setSessionExpired(false);
        sessionExpiredRef.current = false;
    };

    const handleLogout = () => {
        trackUserAction('logout_clicked', 'navigation');
        logout();
    };

    // Function to check if user can access a feature
    const canAccess = (feature: string) => {
        let result = false;
        switch (feature) {
            case 'bonds':
                // Allow public access
                result = true;
                break;
            case 'report-sources':
                // Basic feature - available to FREE, PRO, and ADMIN users
                result = isAuthenticated;
                break;
            case 'web-search':
            case 'search-history':
            case 'search-results':
                // Premium PRO feature - only for PRO (USER) and ADMIN users
                result = isAuthenticated && (hasRole('ADMIN') || hasRole('USER'));
                break;
            case 'content-search':
                // Basic feature - available to FREE, PRO, and ADMIN users  
                result = isAuthenticated;
                break;
            case 'user-management':
                // Only for ADMIN users
                result = isAuthenticated && hasRole('ADMIN');
                break;
            case 'reports':
                // Only for ADMIN users
                result = isAuthenticated && hasRole('ADMIN');
                break;
            case 'experiments':
                // Only for ADMIN users
                result = isAuthenticated && hasRole('ADMIN');
                break;
            case 'search-metrics':
                // Only for ADMIN users - search pipeline metrics dashboard
                result = isAuthenticated && hasRole('ADMIN');
                break;
            case 'report-source-candidates':
                // Available for PRO (USER) and ADMIN users - view URL candidates from scraping
                result = isAuthenticated && (hasRole('ADMIN') || hasRole('USER'));
                break;
            case 'feedback':
                // Available to all authenticated users
                result = isAuthenticated;
                break;
            case 'feedback-management':
                // Only for ADMIN users
                result = isAuthenticated && hasRole('ADMIN');
                break;
            case 'climate-risk':
                // Climate risk assessments - PUBLIC examples for guests, full access for authenticated
                result = true;
                break;
            case 'admin-debug':
                // Only for ADMIN users - debug views for search targets and attempts
                result = isAuthenticated && hasRole('ADMIN');
                break;
            default:
                result = false;
        }
        // console.log('🔐 canAccess:', { feature, result, isAuthenticated, isImpersonating });
        return result;
    };

    const handleLoginModalOpen = (mode: 'login' | 'signup' = 'login') => {
        trackUserAction('login_modal_opened', 'authentication', { mode });
        setModalMode(mode);
        setIsLoginModalOpen(true);
    };

    const handleNavClick = (section: string) => {
        trackUserAction('navigation_clicked', 'navigation', {
            destination: section,
            deviceType: deviceInfo?.deviceType,
        });
    };

    const handleNavigate = (path: string) => {
        trackUserAction('navigation_clicked', 'profile_menu', {
            destination: path,
            deviceType: deviceInfo?.deviceType,
        });
        navigate(path);
    };

    const handleThemeChange = (newTheme: ThemeMode) => {
        setThemeMode(newTheme);

        // Update isDarkMode based on the new theme mode
        if (newTheme === 'light') {
            setIsDarkMode(false);
        } else if (newTheme === 'dark') {
            setIsDarkMode(true);
        } else {
            // 'system' mode - use current system preference
            setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
        }
    };

    // Error boundary for tracking errors
    const ErrorFallback = ({ error, resetError }: { error: unknown; resetError: () => void }) => {
        useEffect(() => {
            trackError(error instanceof Error ? error : new Error(String(error)), 'error_boundary');
        }, [error]);

        return (
            <div className="error-boundary">
                <h2>Something went wrong:</h2>
                <pre>{error instanceof Error ? error.message : String(error)}</pre>
                <button type="button" onClick={resetError}>Try again</button>
            </div>
        );
    };

    const SentryRoutes = Sentry.withSentryRouting(Routes);

    return (
        <>
            <PageViewTracker />
            <div className="App">
                <nav className="main-nav">
                    <div className="nav-brand">
                        <h1>LuLarge</h1>
                    </div>

                    <div className="nav-links desktop-nav-links">
                        <NavLink
                            to="/"
                            className={({ isActive }) => isActive ? 'active' : ''}
                            onClick={() => handleNavClick('home')}
                        >
                            {hasRole('ADMIN') ? 'Dashboard' : t('navigation.home')}
                        </NavLink>
                        {hasRole('ADMIN') && (
                            <>
                                <NavLink to="/reports" className={({ isActive }) => isActive ? 'active' : ''}>{t('navigation.reports')}</NavLink>
                            </>
                        )}
                        {canAccess('bonds') && (
                            <NavLink
                                to="/bonds"
                                className={({ isActive }) => isActive ? 'active' : ''}
                                onClick={() => handleNavClick('bonds')}
                            >
                                {isAuthenticated ? t('navigation.bonds') : t('navigation.exampleData')}
                            </NavLink>
                        )}

                        {canAccess('climate-risk') && (
                            <NavLink
                                to="/climate-risk"
                                className={({ isActive }) => isActive ? 'active' : ''}
                                onClick={() => handleNavClick('climate-risk')}
                            >
                                {isAuthenticated ? t('navigation.climateRisk') : t('navigation.climateRiskExamples')}
                            </NavLink>
                        )}

                        {canAccess('report-sources') && (
                            <NavLink
                                to="/report-sources"
                                className={({ isActive }) => isActive ? 'active' : ''}
                                onClick={() => handleNavClick('report-sources')}
                            >
                                {t('navigation.reportSources')}
                            </NavLink>
                        )}
                        {/* Search Submenu */}
                        {(canAccess('content-search') || canAccess('web-search') || canAccess('search-history') || canAccess('search-results') || canAccess('report-source-candidates') || canAccess('search-metrics') || canAccess('admin-debug')) && (
                            <NavSubmenu
                                label={t('navigation.search') || 'Search'}
                                items={[
                                    ...(canAccess('content-search') ? [{
                                        to: '/content-search',
                                        label: t('navigation.contentSearch'),
                                        onClick: () => handleNavClick('content-search')
                                    }] : []),
                                    ...(canAccess('web-search') ? [{
                                        to: '/web-search',
                                        label: t('navigation.webSearch'),
                                        onClick: () => handleNavClick('web-search')
                                    }] : []),
                                    ...(canAccess('search-history') ? [{
                                        to: '/search-history',
                                        label: t('navigation.searchHistory') || 'Search Targets',
                                        onClick: () => handleNavClick('search-history')
                                    }] : []),
                                    ...(canAccess('search-results') ? [{
                                        to: '/search-results',
                                        label: t('navigation.searchResults') || 'Search Results',
                                        onClick: () => handleNavClick('search-results')
                                    }] : []),
                                    ...(canAccess('report-source-candidates') ? [{
                                        to: '/report-source-candidates',
                                        label: t('navigation.reportSourceCandidates') || 'URL Candidates',
                                        onClick: () => handleNavClick('report-source-candidates')
                                    }] : []),
                                    ...(canAccess('search-metrics') ? [{
                                        to: '/search-metrics',
                                        label: t('navigation.searchMetrics') || 'Search Metrics',
                                        onClick: () => handleNavClick('search-metrics')
                                    }] : []),
                                    ...(canAccess('admin-debug') ? [{
                                        to: '/admin/search-targets',
                                        label: t('navigation.searchTargetsDebug') || 'Search Targets (Debug)',
                                        onClick: () => handleNavClick('admin-search-targets')
                                    }] : []),
                                    ...(canAccess('admin-debug') ? [{
                                        to: '/admin/search-attempts',
                                        label: t('navigation.searchAttemptsDebug') || 'Search Attempts (Debug)',
                                        onClick: () => handleNavClick('admin-search-attempts')
                                    }] : [])
                                ]}
                            />
                        )}
                        {/* Runtime Submenu - Experiments, Tasks & Statistics */}
                        {hasRole('ADMIN') && (
                          <NavSubmenu
                            label={t('navigation.runtime') || 'Runtime'}
                            items={[
                                {
                                    to: '/experiments',
                                    label: t('navigation.experiments') || 'Experiments',
                                    onClick: () => handleNavClick('experiments')
                                },
                                {
                                    to: '/report-experiments',
                                    label: t('navigation.reportExperiments') || 'Report Experiments',
                                    onClick: () => handleNavClick('report-experiments')
                                },
                                {
                                    to: '/task-status',
                                    label: t('navigation.taskStatus') || 'Task Status',
                                    onClick: () => handleNavClick('task-status')
                                },
                                {
                                    to: '/tasks',
                                    label: t('navigation.tasks') || 'Tasks Manager',
                                    onClick: () => handleNavClick('tasks')
                                },
                                {
                                    to: '/stats',
                                    label: t('navigation.stats') || 'Statistics',
                                    onClick: () => handleNavClick('stats')
                                }
                            ]}
                          />
                        )}
                        {/* Admin Submenu - User Management & Feedback */}
                        {hasRole('ADMIN') && (
                          <NavSubmenu
                            label={t('navigation.admin') || 'Admin'}
                            items={[
                                {
                                    to: '/user-management',
                                    label: t('navigation.userManagement') || 'Users',
                                    onClick: () => handleNavClick('user-management')
                                },
                                {
                                    to: '/feedback-management',
                                    label: t('navigation.feedback') || 'Feedback',
                                    onClick: () => handleNavClick('feedback-management')
                                }
                            ]}
                          />
                        )}
                        {hasRole('ADMIN') && (
                            <NavLink to="/ground-truth" className={({ isActive }) => isActive ? 'active' : ''}>{t('navigation.groundTruth') || 'Ground Truth'}</NavLink>
                        )}

                        {/*{canAccess('feedback') && (*/}
                        {/*    <NavLink */}
                        {/*        to="/feedback" */}
                        {/*        className={({ isActive }) => isActive ? 'active' : ''}*/}
                        {/*        onClick={() => handleNavClick('feedback')}*/}
                        {/*    >*/}
                        {/*        Feedback*/}
                        {/*    </NavLink>*/}
                        {/*)}*/}

                        {/* Hide Benefits from main nav for admins and unauthenticated users */}
                        {isAuthenticated && !hasRole('ADMIN') && (
                            <>
                                {hasRole('FREE') ? (
                                    <div className="nav-upgrade-item">
                                        <UpgradeButton
                                            className="nav-upgrade-button-center"
                                            size="small"
                                            variant="primary"
                                            showBenefits={false}
                                        />
                                    </div>
                                ) : (
                                    <NavLink
                                        to="/benefits"
                                        className={({ isActive }) => isActive ? 'active' : ''}
                                        onClick={() => handleNavClick('benefits')}
                                    >
                                        {t('navigation.benefits')}
                                    </NavLink>
                                )}
                            </>
                        )}
                    </div>
                    <div className="nav-controls">
                        {isAuthenticated ? (
                            <ProfileMenu
                                themeMode={themeMode}
                                onThemeToggle={() => {
                                    const nextMode: ThemeMode =
                                        themeMode === 'light' ? 'dark' :
                                            themeMode === 'dark' ? 'system' : 'light';
                                    setThemeMode(nextMode);

                                    // Update isDarkMode based on the new theme mode
                                    if (nextMode === 'light') {
                                        setIsDarkMode(false);
                                    } else if (nextMode === 'dark') {
                                        setIsDarkMode(true);
                                    } else {
                                        // 'system' mode - use current system preference
                                        setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
                                    }
                                }}
                                onLogout={handleLogout}
                                onShowSystemInfo={() => setShowSystemInfoPopup(true)}
                                onNavigate={handleNavigate}
                            />
                        ) : (
                            <>
                                <div className="auth-buttons">
                                    <button
                                        type="button"
                                        className="signup-button"
                                        onClick={() => {
                                            trackUserAction('signup_clicked', 'navigation', { source: 'header' });
                                            navigate('/benefits');
                                        }}
                                    >
                                        {t('auth.getStartedNow')}
                                    </button>
                                    <button
                                        type="button"
                                        className="login-button"
                                        onClick={() => handleLoginModalOpen('login')}
                                    >
                                        {t('auth.login')}
                                    </button>
                                </div>
                                <SettingsMenu
                                    themeMode={themeMode}
                                    onThemeChange={handleThemeChange}
                                    onShowSystemInfo={() => setShowSystemInfoPopup(true)}
                                />
                            </>
                        )}
                    </div>
                </nav>

                {/* Impersonation overlay - shows when admin is impersonating another role */}
                <ImpersonationOverlay />

                {/* Trial status banner - shows trial countdown and search limits for TRIAL users */}
                <TrialStatusBanner />

                {authLoaded && (
                    <Sentry.ErrorBoundary fallback={ErrorFallback}>
                        <SentryRoutes>
                            <Route path="/" element={
                                isAuthenticated ? (
                                    hasRole('ADMIN') ? (
                                        <AdminDashboard />
                                    ) : canAccess('content-search') ? (
                                        <Navigate to="/content-search" replace />
                                    ) : canAccess('bonds') ? (
                                        <Navigate to="/bonds" replace />
                                    ) : canAccess('report-sources') ? (
                                        <Navigate to="/report-sources" replace />
                                    ) : (
                                        <div className="container">
                                            <h1>{t('common.welcome')}</h1>
                                            <p>{t('common.exploreData')}</p>
                                        </div>
                                    )
                                ) : (
                                    <LandingView />
                                )
                            } />
                            <Route path="/home" element={
                                isAuthenticated && hasRole('ADMIN') ? <AdminDashboard /> : <Navigate to="/" replace />
                            } />
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            {/* Test routes for email verification - only in development */}
                            {env.isDev && (
                                <>
                                    <Route path="/test/verify-comprehensive" element={<VerifyEmailTest />} />
                                    <Route path="/test/verify-success" element={
                                        <VerifyEmail testStatus="success" />
                                    } />
                                    <Route path="/test/verify-error" element={
                                        <VerifyEmail testStatus="error" testError="This is a test error message for verification failure." />
                                    } />
                                    <Route path="/test/verify-loading" element={
                                        <VerifyEmail testStatus="verifying" />
                                    } />
                                    <Route path="/test/verify-invalid-token" element={
                                        <VerifyEmail testStatus="error" testError="Invalid verification token provided." />
                                    } />
                                    <Route path="/test/verify-expired" element={
                                        <VerifyEmail testStatus="error" testError="Verification token has expired. Please request a new verification email." />
                                    } />
                                </>
                            )}
                            <Route path="/bonds" element={
                                canAccess('bonds') ? <BondsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/bonds/:id" element={
                                canAccess('bonds') ? <BondDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/climate-risk" element={
                                canAccess('climate-risk') ? <ClimateRiskView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/climate-risk/:id" element={
                                canAccess('climate-risk') ? <ClimateRiskDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/web-search" element={
                                canAccess('web-search') ? <WebSearchView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/search-history" element={
                                canAccess('search-history') ? <SearchTargetsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/search-results" element={
                                canAccess('search-results') ? <SearchResultsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/search-metrics" element={
                                canAccess('search-metrics') ? <SearchMetricsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/report-source-candidates" element={
                                canAccess('report-source-candidates') ? <ReportSourceCandidatesView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/report-source-candidates/:id" element={
                                canAccess('report-source-candidates') ? <ReportSourceCandidateDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/report-sources" element={
                                canAccess('report-sources') ? <ReportSourcesView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/content-search" element={
                                canAccess('content-search') ? <EmbeddingsSearchView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/user-management" element={
                                canAccess('user-management') ? <UserManagementView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/reports" element={
                                canAccess('reports') ? <ReportsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/reports/:id" element={
                                canAccess('reports') ? <ReportDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/experiments" element={
                                canAccess('experiments') ? <ExperimentsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/experiments/new" element={
                                canAccess('experiments') ? <ExperimentCreateEdit /> : <Navigate to="/" replace />
                            } />
                            <Route path="/experiments/:id" element={
                                canAccess('experiments') ? <ExperimentDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/experiments/:id/edit" element={
                                canAccess('experiments') ? <ExperimentCreateEdit /> : <Navigate to="/" replace />
                            } />
                            <Route path="/report-experiments" element={
                                canAccess('experiments') ? <ReportExperimentsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/stats" element={
                                hasRole('ADMIN') ? <StatsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/task-status" element={
                                hasRole('ADMIN') ? <TaskStatusView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/tasks" element={                                hasRole('ADMIN') ? <TasksView /> : <Navigate to="/" replace />                            } />
                            <Route path="/tasks/:id" element={
                                hasRole('ADMIN') ? <TaskDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/ground-truth" element={
                                hasRole('ADMIN') ? <GroundTruthListView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/ground-truth/:id" element={
                                hasRole('ADMIN') ? <GroundTruthDetailView /> : <Navigate to="/" replace />
                            } />
                            {/* Admin Debug Views */}
                            <Route path="/admin/search-targets" element={
                                canAccess('admin-debug') ? <AdminSearchTargetsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/admin/search-targets/:id" element={
                                canAccess('admin-debug') ? <AdminSearchTargetDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/admin/search-targets/:id/pipeline" element={
                                canAccess('admin-debug') ? <SearchTargetPipelineView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/admin/search-attempts" element={
                                canAccess('admin-debug') ? <AdminSearchAttemptsView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/admin/search-attempts/:id" element={
                                canAccess('admin-debug') ? <AdminSearchAttemptDetailView /> : <Navigate to="/" replace />
                            } />
                            <Route path="/feedback" element={
                                canAccess('feedback') ? <Feedback /> : <Navigate to="/" replace />
                            } />
                            <Route path="/feedback-management" element={
                                canAccess('feedback-management') ? <FeedbackManagement /> : <Navigate to="/" replace />
                            } />
                            <Route path="/settings" element={
                                isAuthenticated ? (
                                    <SettingsView
                                        themeMode={themeMode}
                                        onThemeChange={handleThemeChange}
                                    />
                                ) : <Navigate to="/" replace />
                            } />
                            {/* Subscription routes */}
                            <Route path="/subscription/success" element={
                                isAuthenticated ? <SubscriptionSuccess /> : <Navigate to="/" replace />
                            } />
                            <Route path="/subscription/canceled" element={
                                isAuthenticated ? <SubscriptionCanceled /> : <Navigate to="/" replace />
                            } />
                            <Route path="/subscription/manage" element={
                                isAuthenticated ? <SubscriptionManage /> : <Navigate to="/" replace />
                            } />
                            {/* Benefits route - accessible to everyone */}
                            <Route path="/benefits" element={<BenefitsView />} />
                            {/* Privacy Policy - accessible to everyone */}
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            {/* Imprint - accessible to everyone */}
                            <Route path="/imprint" element={<Imprint />} />
                        </SentryRoutes>
                    </Sentry.ErrorBoundary>
                )}

                <LoginModal
                    isOpen={isLoginModalOpen}
                    onClose={() => {
                        trackUserAction('login_modal_closed', 'authentication');
                        setIsLoginModalOpen(false);
                    }}
                    onLogin={login}
                    apiBaseUrl={apiBaseUrl}
                    mode={modalMode}
                    onModeChange={setModalMode}
                />

                {sessionExpired && (
                    <LoginModal
                        isOpen={true}
                        onClose={() => { setSessionExpired(false); sessionExpiredRef.current = false; }}
                        onLogin={handleLogin}
                        apiBaseUrl={apiBaseUrl}
                        mode="login"
                    />
                )}

                <CookieBanner onConsentChange={handleCookieConsentChange} />

                {/* System Info Popup */}
                <SystemInfoPopup
                    isOpen={showSystemInfoPopup}
                    onClose={() => setShowSystemInfoPopup(false)}
                    versionInfo={versionInfo}
                />

                {/* Footer with legal links */}
                <footer className="app-footer">
                    <div className="footer-links">
                        <NavLink to="/privacy" onClick={() => handleNavClick('privacy')}>
                            {t('privacy.title')}
                        </NavLink>
                        <span className="footer-separator">|</span>
                        <NavLink to="/imprint" onClick={() => handleNavClick('imprint')}>
                            {t('imprint.title')}
                        </NavLink>
                        <span className="footer-separator">|</span>
                        <a href="mailto:support@lularge.com">{t('privacy.contactUs')}</a>
                        <button
                            type="button"
                            className="info-icon-button footer-version-info"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowSystemInfoPopup(true);
                                return false;
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                            }}
                            title={t('common.versionInformation')}
                            style={{ marginLeft: 8 }}
                        >
                            <span className="material-icons">info</span>
                        </button>
                    </div>
                </footer>

                {/* Bottom Navigation for Mobile */}
                <BottomNavigation onNavClick={handleNavClick} />
            </div>
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <AppContent />
            </Router>
        </AuthProvider>
    );
}

export default App
